import { useConfigStore } from '@/stores';
import './global.css';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import nextSvg from '@/assets/images/next.svg';
import prevSvg from '@/assets/images/prev.svg';
import { generateFloorName } from '@/utils/floor-guide';
import { useTickSoundInteraction } from '@/hooks';
import { JAMSIL_MAP_IDS } from '@/constants/app';
import { template_0001 } from './0001';
import { template_0005 } from './0005';
import { template_0002 } from './0002';
import { template_0006 } from './0006';

interface AllFloorsGuideProps {
    onSelectFloor?: (floorName: string, floorId?: string, buidingId?: string, storeCode?: string) => void;
}

const HTML_BY_STORE_CODE: Record<string, string> = {
    '0001': template_0001,
    '0005': template_0005,
    '0002': template_0002,
    '0006': template_0006, // 0006 is the same as 0001
};

const FLOORTEXT = {
    '7F': {
        'ko': '뮤지엄',
        'en': 'Museum',
        'zh-CN': '博物馆',
        'zh-TW': '博物館',
        'ja': 'ミュージアム'
    },
    '30F': {
        'ko': '워크플렉스 롯데월드타워',
        'en': 'Workflex Lotte World Tower',
        'zh-CN': 'Workflex Lotte World Tower',
        'zh-TW': 'Workflex Lotte World Tower',
        'ja': 'Workflex Lotte World Tower'
    },
    '76F': {
        'ko': '시그니엘 서울',
        'en': 'Signiel Seoul',
        'zh-CN': 'Signiel Seoul',
        'zh-TW': 'Signiel Seoul',
        'ja': 'シグニエルソウル'
    },
    '117F': {
        'ko': 'SEOUL SKY (전망대)',
        'en': 'Observatory',
        'zh-CN': '觀景臺',
        'zh-TW': '观景台',
        'ja': '展望台'
    },
    '2F': {
        'ko': '로비',
        'en': 'Lobby',
        'zh-CN': '大廳',
        'zh-TW': '大厅',
        'ja': 'ロビー'
    }

} as const;

export const AllFloorsGuide = ({ onSelectFloor }: AllFloorsGuideProps) => {
    const { t, i18n } = useTranslation();
    const [staticHtml, setStaticHtml] = useState<string>('');
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const currentBuilding = useConfigStore((state) => state.config?.server?.building);
    const orientation = document.body.dataset.orientation;
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { playTick } = useTickSoundInteraction();
    const mapId = useConfigStore((state) => state?.config?.server?.store?.studioMapId) || '';
    const isJamsil = JAMSIL_MAP_IDS.includes(mapId);
    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode);
    const mapHtml = (storeCode && HTML_BY_STORE_CODE[storeCode]) || template_0006;

    // Click handler for regular floors
    const handleFloorClick = (event: Event) => {
        const target = event.target as HTMLElement;
        const floorEl = target.closest('.floor-guide-floor') as HTMLElement;
        const floorNoClick = target.closest('.floor-guide-no-click') as HTMLElement;

        if (!floorEl || floorNoClick) return;

        const buildingEl = floorEl.closest('.floor-guide-floors') as HTMLElement;
        if (!buildingEl) return;

        const storeCode = buildingEl.getAttribute('id')?.replace('id-', '') || '';
        const building = buildings?.find((b) => b.storeCode === storeCode);
        const buildingId = building?.studioBuildingId?.replace(`_${storeCode}`, '') || '';
        // Find building and floor info

        const floorNameEl = floorEl.querySelector('.floor-guide-floor-name');

        if (onSelectFloor) {
            if (!isJamsil) {
                onSelectFloor(floorNameEl?.textContent || '');
            } else {
                onSelectFloor(floorNameEl?.textContent || '', floorEl?.getAttribute('data-floor-id') || '', buildingId || '', storeCode);
            }
        }
    };

    // Click handler for gray floors
    const handleGrayFloorClick = (event: Event) => {
        const target = event.target as HTMLElement;
        const floorEl = target.closest('.floor-guide-floor--gray') as HTMLElement;
        if (!floorEl) return;

        const buildingEl = floorEl.closest('.floor-guide-floors') as HTMLElement;
        if (!buildingEl) return;
        const storeCode = buildingEl.getAttribute('id')?.replace('id-', '') || '';
        const building = buildings?.find((b) => b.storeCode === storeCode);
        const buildingId = building?.studioBuildingId?.replace(`_${storeCode}`, '') || '';
        // Find building and floor info

        if (onSelectFloor) {
            if (!isJamsil) {
                onSelectFloor(floorEl.textContent || '');
            } else {
                onSelectFloor(floorEl.textContent || '', floorEl.getAttribute('data-floor-id') || '', buildingId || '', storeCode);
            }
        }
    };

    useEffect(() => {
        const container = nodeRef.current;

        if (!container || !currentBuilding || !mapHtml) return;

        // Đổ static HTML gốc vào theo storeCode
        container.innerHTML = mapHtml;
        // Duyệt qua từng building trong dữ liệu JSON
        buildings?.forEach((building) => {

            const buildingEl = container.querySelector(
                !isJamsil
                    ? `#${building?.name?.en?.toLowerCase().replace(/\s+/g, "")}`
                    : `#${currentBuilding?.name?.en?.toLowerCase().replace(/\s+/g, "")}`
            ) ?? container.querySelector('.floor-guide-floors');

            if (!buildingEl) return; // không có trong DOM tĩnh

            const buidlingIndex = String(currentBuilding?.name?.en?.toLocaleLowerCase()) === buildingEl.getAttribute('id') ? 1 : 2;

            const tabIndexDefault = orientation == 'vertical' ? 1 : 10;

            const mallNameEl = buildingEl.querySelector(".floor-guide-mall-name-tts");

            if (mallNameEl) {
                const buidlingName = building.name?.[i18n.language] || building.name?.ko || "";
                mallNameEl.textContent = buidlingName;
                mallNameEl.setAttribute('aria-label', buidlingName);
                buildingEl.setAttribute('data-region-nav', 'true');
                buildingEl.setAttribute('data-region-order', `${tabIndexDefault + buidlingIndex}`);
                mallNameEl.setAttribute('tabindex', `${tabIndexDefault + buidlingIndex}`);
                mallNameEl.classList.add('focus-border-floor');
                mallNameEl.setAttribute('data-interactive', 'true');
            }

            building.floors.forEach((floor) => {
                const floorEl = Array.from(
                    buildingEl.querySelectorAll(".floor-guide-floor")
                ).find(
                    (el) =>
                        el.querySelector(".floor-guide-floor-name")?.textContent?.trim() ===
                        floor.name.ko
                );
                if (floorEl) {
                    // floorEl.setAttribute('tabindex', tabindex.toString());
                    floorEl.setAttribute('data-interactive', 'true');
                    floorEl.classList.add('focus-border-floor');
                    floorEl.setAttribute('data-role', t('common.data_role.button'));
                    floorEl.setAttribute('tabindex', `${tabIndexDefault + buidlingIndex}`);
                    // Cập nhật mô tả (theo đúng ngôn ngữ)
                    const descEl = floorEl.querySelector(".floor-guide-floor-desc");
                    floorEl.setAttribute('data-floor-id', floor.floorCode);
                    floorEl.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name.ko ?? ''), }) || ''}, ${descEl?.textContent}`);
                    if (descEl && floor.detail) {
                        const floorDesc = floor.detail[i18n.language];
                        descEl.textContent = floorDesc;
                        floorEl.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name.ko ?? ''), }) || ''}, ${floorDesc}`);
                    } else if (i18n.language !== 'ko' && building.storeCode === '0500' && isJamsil) {
                        switch (floor.name.ko) {
                            case '31F':
                                const text = "SKY 31 Convention";
                                descEl!.textContent = text;
                                floorEl.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name.ko ?? ''), }) || ''}, ${text}`);
                                break;
                            case '12F':
                                const text12 = "Finance Center";
                                descEl!.textContent = text12;
                                floorEl.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name.ko ?? ''), }) || ''}, ${text12}`);
                                break;
                            case '11F':
                                const text11 = "Medical Center";
                                descEl!.textContent = text11;
                                floorEl.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name.ko ?? ''), }) || ''}, ${text11}`);
                                break;
                            case '7F':
                                const text7 = FLOORTEXT[floor.name.ko][i18n.language as keyof typeof FLOORTEXT['7F']] || FLOORTEXT[floor.name.ko]['en'];
                                descEl!.textContent = text7;
                                floorEl.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name.ko ?? ''), }) || ''}, ${text7}`);
                                break;
                        }
                    }
                } else {
                    const floorElGray = buildingEl.querySelector(".floor-guide-floor--gray")
                    if (!floorElGray) return;
                    floorElGray?.setAttribute('data-interactive', 'true');
                    floorElGray.setAttribute('data-role', t('common.data_role.button'));
                    floorElGray?.classList.add('focus-border-floor');
                    floorElGray?.setAttribute('tabindex', `${tabIndexDefault + buidlingIndex}`);
                    const descEl = floorElGray?.querySelector(".floor-guide-floor-desc");
                    floorElGray?.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName('B2-B7'), }) || ''}, ${descEl?.textContent}`);
                }
            });

            if (building.storeCode === '0500' && isJamsil) {
                const floorEl = Array.from(
                    buildingEl.querySelectorAll(".floor-guide-floor")
                ).find(
                    (el) =>
                        el.querySelector(".floor-guide-floor-name")?.textContent?.trim() === '2F'
                );

                const descEl = floorEl?.querySelector(".floor-guide-floor-desc");
                const text2 = FLOORTEXT['2F'][i18n.language as keyof typeof FLOORTEXT['2F']] || FLOORTEXT['2F']['en'];
                if (descEl) {
                    descEl.textContent = text2;
                }
                floorEl?.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName('2F'), }) || ''}, ${text2}`);

                const floorEl2 = Array.from(
                    buildingEl.querySelectorAll(".floor-guide-floor")
                ).find(
                    (el) =>
                        el.querySelector(".floor-guide-floor-name")?.textContent?.trim().includes('76F')
                );

                const descEl2 = floorEl2?.querySelector(".floor-guide-floor-desc");
                const text3 = FLOORTEXT['76F'][i18n.language as keyof typeof FLOORTEXT['76F']] || FLOORTEXT['76F']['en'];
                if (descEl2) {
                    descEl2.textContent = text3;
                }
                floorEl2?.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName('76F - 101F'), }) || ''}, ${text3}`);

                const floorEl3 = Array.from(
                    buildingEl.querySelectorAll(".floor-guide-floor")
                ).find(
                    (el) =>
                        el.querySelector(".floor-guide-floor-name")?.textContent?.trim().includes('117F')
                );

                const descEl3 = floorEl3?.querySelector(".floor-guide-floor-desc");
                const text4 = FLOORTEXT['117F'][i18n.language as keyof typeof FLOORTEXT['117F']] || FLOORTEXT['117F']['en'];
                if (descEl3) {
                    descEl3.textContent = text4;
                }
                floorEl3?.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName('117F - 123F'), }) || ''}, ${text4}`);

                const floorEl4 = Array.from(
                    buildingEl.querySelectorAll(".floor-guide-floor")
                ).find(
                    (el) =>
                        el.querySelector(".floor-guide-floor-name")?.textContent?.trim().includes('30F')
                );

                const descEl4 = floorEl4?.querySelector(".floor-guide-floor-desc");
                const text5 = FLOORTEXT['30F'][i18n.language as keyof typeof FLOORTEXT['30F']] || FLOORTEXT['30F']['en'];
                if (descEl4) {
                    descEl4.textContent = text5;
                }
                floorEl3?.setAttribute('aria-label', `${t('floor_guide.text_to_speech.floor', { floorName: generateFloorName('30F'), }) || ''}, ${text5}`);
            }
        });

        setStaticHtml(container.innerHTML);

    }, [buildings, i18n, mapHtml, currentBuilding]);

    useEffect(() => {
        setTimeout(() => {
            const container = nodeRef.current;
            if (!container || !currentBuilding) return;

            const buildingEl =
                container.querySelector(
                    !isJamsil
                        ? `#${currentBuilding?.name?.en?.toLowerCase().replace(/\s+/g, "")}`
                        : `#${currentBuilding?.name?.en?.toLowerCase().replace(/\s+/g, "")}`
                ) ?? container.querySelector('.floor-guide-floors');

            buildingEl?.scrollIntoView({
                behavior: "smooth",   // cuộn mượt
                block: "center",      // căn giữa theo chiều dọc
                inline: "center"      // căn giữa theo chiều ngang (nếu phần tử nằm ngang)
            });
            if (orientation == 'vertical') {
                const b = document.querySelector('.all-floor-guide-title') as HTMLElement;
                b.focus();
            } else {
                const a = buildingEl?.querySelector('.floor-guide-mall-name-tts') as HTMLElement;
                a.focus();
            }
        }, 300);
    }, [staticHtml, currentBuilding, isJamsil, orientation])

    // Add click event listeners after HTML is rendered
    useEffect(() => {
        const container = nodeRef.current;
        if (!container) return;

        // Add click listeners to all floor elements
        const floorElements = container.querySelectorAll('.floor-guide-floor:not(.floor-guide-floor--gray)');

        const handleKeyDown = (e: Event) => {
            const event = e as KeyboardEvent;
            if (event.key === 'Enter') {
                playTick();
                handleFloorClick(event);
            }
        };

        floorElements.forEach((floorEl) => {
            // Remove existing listeners to avoid duplicates
            floorEl.removeEventListener('click', handleFloorClick);
            floorEl.removeEventListener('keydown', handleKeyDown);

            // Add new listener
            floorEl.addEventListener('click', handleFloorClick);
            floorEl.addEventListener('keydown', handleKeyDown);

        });

        // Add click listeners to gray floor elements
        const grayFloorElements = container.querySelectorAll('.floor-guide-floor--gray');
        grayFloorElements.forEach((floorEl) => {
            // floorEl.removeEventListener('click', handleGrayFloorClick);
            // floorEl.removeEventListener('keydown', handleKeyDown);

            // floorEl.addEventListener('click', handleGrayFloorClick);
            // floorEl.addEventListener('keydown', handleKeyDown);


        });

        // Cleanup function
        return () => {
            floorElements.forEach(floorEl => {
                floorEl.removeEventListener('click', handleFloorClick);
                floorEl.removeEventListener('keydown', handleKeyDown)
            });
            grayFloorElements.forEach(floorEl => {
                floorEl.removeEventListener('click', handleGrayFloorClick);
                floorEl.removeEventListener('keydown', handleKeyDown)
            });
        };
    }, [staticHtml, onSelectFloor]);

    if (!mapHtml) {
        return null;
    }

    return (
        <div className="floor-guide-container all-floors-scroll-wrapper no-scrollbar" ref={containerRef}>
            {buildings && buildings?.length > 1 &&
                <>
                    <img
                        className="floor-guide-arrow floor-guide-arrow--left"
                        src={prevSvg}
                        alt="Previous"
                        style={{ pointerEvents: 'none' }}
                    />
                    <img
                        className="floor-guide-arrow floor-guide-arrow--right"
                        src={nextSvg}
                        alt="Next"
                        style={{ pointerEvents: 'none' }}
                    />
                </>}
            <div
                ref={nodeRef}

                className={`floor-guide-drag-content`}
                dangerouslySetInnerHTML={{ __html: staticHtml }}
            />
        </div>
    );
};
